import React, { FC, ReactNode } from "react";
import { Language, translations, TranslationKeys } from "./translations";

type TranslationSet = Record<TranslationKeys, string>;

export type FancyContextProps = {
	lang: Language;
	formattingLang: string;
	staticMode?: boolean;
	translationOverrides?: Partial<TranslationSet>;
	suppressDeprecationWarnings?: boolean;
};
const createCtx = () =>
	React.createContext<FancyContextProps>({
		lang: "en-US",
		formattingLang: "en-US",
	});
const GlobalFancyContextKey = "__globalFancyContext";

declare global {
	interface Window {
		[GlobalFancyContextKey]?: React.Context<FancyContextProps>;
	}
}

/* This context needs to be globally the same because clients might use code splitting (e.g. pfg) but the context needs to point to the exact same value */

let localCtx: React.Context<FancyContextProps> | null = null;
export const getOrCreateGlobalFancyContext = (): React.Context<FancyContextProps> => {
	//check for window because of SSR, in SSR we don't care about the context being global
	if (typeof window !== "undefined") {
		const ctx = window[GlobalFancyContextKey];
		//use existing if exist, otherwise create new and save in window
		return ctx ?? (window[GlobalFancyContextKey] = createCtx());
	} else {
		//use existing if exist, otherwise create new and save in local
		return localCtx ?? (localCtx = createCtx());
	}
};

export type FancyContextProviderProps = FancyContextProps & { children?: ReactNode };

export function FancyContextProvider(props: FancyContextProviderProps): JSX.Element {
	const { children, ...ctxValue } = props;
	const fancyCtx = getOrCreateGlobalFancyContext();
	return <fancyCtx.Provider value={ctxValue}>{children}</fancyCtx.Provider>;
}

export const FancyRoot = (props: FancyContextProviderProps): JSX.Element => (
	<div className="fancy-root">
		<FancyContextProvider {...props} />
	</div>
);

export function useTranslations(): Record<TranslationKeys, string> {
	const { lang, translationOverrides } = React.useContext(getOrCreateGlobalFancyContext());
	if (translationOverrides) {
		return { ...translations[lang], ...translationOverrides };
	} else {
		return translations[lang];
	}
}

export function useFormattingLanguage(): string {
	const { formattingLang } = React.useContext(getOrCreateGlobalFancyContext());
	return formattingLang;
}

export function useStaticMode(): boolean {
	const { staticMode } = React.useContext(getOrCreateGlobalFancyContext());
	return staticMode ?? false;
}

export function useDeprecationWarning<X, Y>(
	component: FC<X>,
	sinceVersion: string,
	useInstead?: FC<Y>
): void {
	const { suppressDeprecationWarnings = true } = React.useContext(getOrCreateGlobalFancyContext());
	!suppressDeprecationWarnings &&
		console.warn(
			`Since ${sinceVersion}, ${
				component.displayName || component.name
			} is no longer supported and it will be completely removed soon.${
				useInstead && ` Please, use ${useInstead.displayName || useInstead.name} instead.`
			}`
		);
}
